import { readableReason } from "@/helpers/humanText";
import axios from "@/plugins/axios";

// A brokerage admin's Held and Dead email queues. The API scopes every
// call to the admin's own brokerage.
const QUEUE_URL = "/intake/brokerage/queue";

export const PAGE_SIZE = 25;
export const KINDS = ["held", "dead"];

// The API's message for a failed call, or a fallback.
const apiMessage = (error, fallback) => readableReason(error?.response?.data?.message, fallback);

const emptyQueue = () => ({ items: [], total: 0, page: 1 });

// The newest fetchQueue request per kind. A reply to an older request is dropped, so a slow
// page 1 never overwrites page 2 (or the other way round), and loading clears only when
// the newest request finishes.
const latestRequest = { held: 0, dead: 0 };

// Posts with the global error toast off and throws an Error carrying the API's message,
// so the dialog can show it.
const postOrThrow = async (url, body, fallback) => {
  let response;
  try {
    response = await axios.post(url, body, { withoutErrorHandler: true });
  } catch (error) {
    // The status rides along so the page can tell a stale item (404, 409) from other failures.
    throw Object.assign(new Error(apiMessage(error, fallback)), { status: error?.response?.status });
  }
  if (!response.data?.success) {
    throw new Error(readableReason(response.data?.message, fallback));
  }
  return response.data;
};

export default {
  namespaced: true,

  state: () => ({
    queues: { held: emptyQueue(), dead: emptyQueue() },
    // Per kind: both tabs load in parallel.
    loading: { held: false, dead: false },
  }),

  mutations: {
    SET_QUEUE(state, { kind, items, total, page }) {
      state.queues = { ...state.queues, [kind]: { items, total, page } };
    },

    SET_LOADING(state, { kind, loading }) {
      state.loading = { ...state.loading, [kind]: loading };
    },
  },

  actions: {
    // A failed load shows the interceptor's toast and rethrows. A reply to a request that a
    // newer one replaced is dropped (returns null).
    async fetchQueue({ commit }, { kind, page = 1 }) {
      const request = ++latestRequest[kind];
      const isLatest = () => request === latestRequest[kind];
      commit("SET_LOADING", { kind, loading: true });
      try {
        const response = await axios.get(QUEUE_URL, { params: { kind, page } });
        if (!isLatest()) return null;
        const data = response.data || {};
        const queue = { kind, items: data.items || [], total: data.total || 0, page: data.page || page };
        commit("SET_QUEUE", queue);
        return queue;
      } catch (error) {
        // An older request that failed after a newer one started is no longer of interest.
        if (!isLatest()) return null;
        throw error;
      } finally {
        if (isLatest()) commit("SET_LOADING", { kind, loading: false });
      }
    },

    // Returns { broker, created, invited, released }. invited: true sent, false not sent, null not due.
    async addBroker(_context, { id, name }) {
      const body = name && name.trim() ? { name: name.trim() } : {};
      return postOrThrow(`${QUEUE_URL}/${id}/add-broker`, body, "The sender could not be added.");
    },

    // Releases only this one email from a sender who already is one of the brokerage's brokers.
    // Returns { entity_id, route, sender_email }.
    async accept(_context, { id }) {
      return postOrThrow(`${QUEUE_URL}/${id}/accept`, {}, "The email could not be accepted.");
    },

    async discard(_context, { id, reason }) {
      return postOrThrow(`${QUEUE_URL}/${id}/discard`, { reason }, "The email could not be discarded.");
    },
  },
};
